import type { RequestInput } from '@setplex/tria-api'
import { isToday, lightFormat } from 'date-fns'
import { Logger, LEVELS } from '~/shared/logger'
import {
  BACKOFF_FACTOR,
  BACKOFF_LIMIT_MILLIS,
  INTERVAL_MILLIS,
  RANDOM_FACTOR,
} from './index.h'

export const logger = new Logger('tria/api/policies', undefined, LEVELS.silent)

/**
 * Extract request URL from `RequestInput` to `URL`
 */
export function toURL(input: RequestInput | Response): URL | undefined {
  const info: Request | Response | URL | string =
    input != null && typeof input === 'object' && 'input' in input
      ? input.input
      : input

  if (info instanceof URL) {
    return info
  }

  try {
    if (info instanceof Request || info instanceof Response) {
      return new URL(info.url)
    }

    const full = info.startsWith('http://') || info.startsWith('https://')
    return new URL(info, full ? undefined : location.href)
  } catch (e) {
    logger.warn('Failed to parse URL from input:', e)
  }
}

/**
 * Get group name for the API request URL
 * @see https://setplexapps.atlassian.net/wiki/spaces/MW/pages/3430776834/Request+Policy#Request-Grouping
 */
export function getGroup(url: string) {
  let paths: string[]

  // check for versioned api endpoints
  paths = url.split('/api/v', 2)
  if (paths.length === 2) {
    paths = paths[1].split('/')
    return 'v' + paths[0] + '/' + paths[1]
  }

  // check for v1 api endpoints
  paths = url.split('/api/', 2)
  if (paths.length === 2) {
    return 'v1/' + paths[1].split('/')[0]
  }

  // default to auth group
  return 'auth'
}

/**
 * Get Request Policy bucket from `RequestInput`
 * @see https://setplexapps.atlassian.net/wiki/spaces/MW/pages/3430776834/Request+Policy#Request-Grouping
 */
export function getBucketName(url?: URL): string | undefined {
  const host = location.host

  // ignore requests to other hosts
  if (url == null || url.host !== host) return

  // ignore requests others than WBS API
  if (!url.pathname.startsWith('/wbs/')) return

  // get bucket name from URL
  // `localStorage` is used to persist buckets across sessions,
  // and it is already separated by hosts by design,
  // so there is no need to include host in the group name
  return getGroup(url.pathname)
}

/**
 * Extract abort signal from `RequestInput`, if any
 */
export function getSignal(input: RequestInput): AbortSignal | undefined {
  const info: Request | URL | string =
    input != null && typeof input === 'object' && 'input' in input
      ? input.input
      : input

  const init =
    input != null && typeof input === 'object' && 'init' in input
      ? input.init
      : undefined

  const infoSignal = info instanceof Request ? info.signal : undefined
  const initSignal = init && init.signal ? init.signal : undefined

  if (infoSignal && initSignal) {
    return AbortSignal.any([infoSignal, initSignal])
  }

  return infoSignal || initSignal
}

/**
 * Calculate backoff in milliseconds based on the attempt count
 *
 * Based on Firebase SDK backoff calculation methof:
 * https://github.com/firebase/firebase-js-sdk/blob/7481098d47d14acce901fa4c065ceff0cbf07d3d/packages/util/src/exponential_backoff.ts
 *
 * Which is based on backoff method from Google Closure Library:
 * https://github.com/google/closure-library/blob/master/closure/goog/math/exponentialbackoff.js
 */
export function calculateBackoffMillis(count: number, logger?: Logger): number {
  const base = Math.round(INTERVAL_MILLIS * Math.pow(BACKOFF_FACTOR, count))
  const fuzz = Math.round(RANDOM_FACTOR * base * (Math.random() - 0.5) * 2)
  const result = base + fuzz
  const ret = Math.min(BACKOFF_LIMIT_MILLIS, Math.max(INTERVAL_MILLIS, result))
  logger?.debug(`calculate backoff: count = ${count}, base = ${base}, fuzz = ${fuzz}, result = ${result}, backoff => ${ret}`) // biome-ignore format: one line
  return ret
}

/**
 * Format timestamp to a human-readable string for debugging
 */
export function format(timestamp: number): string {
  return lightFormat(
    timestamp,
    isToday(timestamp) ? 'HH:mm:ss.SSS' : 'dd.MM.yyyy HH:mm:ss.SSS'
  )
}
